import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTextArea, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import FadedDiv from "src/components/Animations/fade";
import InfiniteScrollWithoutFetch from "src/components/InfiniteScrollWithoutFetch";
import Attachments from "src/components/Attachments";
import Photo from "src/components/Photo";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { convertIFileDTOArrayToFileArray } from "src/Interfaces/fileDTO";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import BadgeEfficiency from "./badge";
import { IEfficiency, IComponentEfficiency } from "./interface";
import ModalAddEfficiency, { IActionAddEfficiencyRef } from "./modalAddEfficiency";
import { ColumnFlex, ContainerUser, SpanComment } from "./styles";

const Efficiency: React.FC<IComponentEfficiency> = ({
	data,
	actionId,
	actionName,
	disabled,
	subtitle,
	loadQuery
}) => {
	const { addToast } = RCCHookToast();
	const { user, superUser, administrator } = useAuth();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	const {confirm} = useConfirmModal();
	const {translate, currentLanguage} = useTranslation();
	const [efficiency, setEfficiency] = useState<IEfficiency[]>(data);
	
	const modalAddEfficiencyRef = useRef<IActionAddEfficiencyRef>(null);

	const {register, setValue, handleSubmit} = useForm();

	const submitDelete = (id: number) => {	
		confirm(
			translate("Exclusão de eficácia"), 
			`${translate("Deseja realmente excluir a eficácia")}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Actions/DeleteEfficiency",
					urlParameters: {
						actionId,
						actionName,
						efficiencyId: id
					}
				});
				addToast({
					color: "green",
					description: translate("Eficácia excluída com sucesso"),
					title: translate("Sucesso")
				});
	
				setEfficiency((prevState) => prevState.filter((inb007) => inb007.inb007Id !== id));
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir eficácia"), addToast);
			},
			true
		);
	};
	
	const openAttachments = async (id: number, index?: number, attachmentsAlreadyLoaded: boolean = false, enableEdit: boolean = false) => {
		if(typeof index === "undefined")
			return;

		let attachments: File[] = [];

		if(!attachmentsAlreadyLoaded){
			setEfficiency((prevState) => {
				const updatedState = [...prevState];					
				updatedState[index].buttonLoading = true;
				return updatedState;
			});
			try{	
				const response = await RCCServicesApi.getRequest({
					method: "Actions/SelectEfficiencyAttachments",
					urlParameters: {
						id
					}
				});
				let dataFormatted: File[] = [];

				if(response.data && response.data.length > 0)
					dataFormatted = convertIFileDTOArrayToFileArray(response.data);

				attachments = dataFormatted;

			}catch(err){
				standardErrorMessages(err, translate("Erro ao buscar anexos"), addToast);
			}
		}

		setEfficiency((prevState) => {
			const updatedState = [...prevState];

			if(!attachmentsAlreadyLoaded){
				updatedState[index].attachments = attachments;
				updatedState[index].buttonLoading = false;
			}

			if(enableEdit)
				updatedState[index].enableEdit = enableEdit;

			updatedState[index].showAttachments = typeof updatedState[index].showAttachments === "undefined" ? true : !updatedState[index].showAttachments;
			updatedState[index].attachmentsAlreadyLoaded = true;
			return updatedState;
		});
	};

	const openEdit = (id: number, index?: number, attachmentsAlreadyLoaded: boolean = false) => {
		if(typeof index === "undefined")
			return;

		if(typeof efficiency[index].showAttachments === "undefined" || !efficiency[index].showAttachments){
			openAttachments(id, index, attachmentsAlreadyLoaded, true);
			return;
		}

		setEfficiency((prevState) => {
			const updatedState = [...prevState];


			updatedState[index].enableEdit = typeof updatedState[index].enableEdit === "undefined" ? true : !updatedState[index].enableEdit;
			return updatedState;
		});	
	};

	const onSubmit = async (dataForm: any, id: number) => {
		const dataFormFiltered = {
			description: dataForm.description[id],
			attachments: dataForm.attachments ? dataForm.attachments[id] || [] : []
		};
		try{
			if(dataFormFiltered.description === ""){
				addToast({
					title: translate("Erro"),
					description: translate("Descrição é requerido"),
					color: "yellow"
				});
				return;
			}

			const formData = new FormData();
			formData.append("id", id.toString());
			formData.append("description", dataFormFiltered.description.toString());
			formData.append("actionName", actionName);
			dataFormFiltered.attachments.forEach((attach: any, index: number) => {
				formData.append(`Attachments[${index}]`, attach);
			});

			await RCCServicesApi.putRequest({
				method: "Actions/EditEfficiency",
				contentType: "multipart/form-data",
				body: formData
			});

			addToast({
				color: "green",
				description: translate("Histórico alterado com sucesso"),
				title: translate("Sucesso")
			});
				
			setEfficiency((prevState) => {
				const updatedState = [...prevState];
				const index = updatedState.findIndex((eff) => eff.inb007Id === id);
				if(index === -1)
					return updatedState;

				updatedState[index].inb007Descricao = dataFormFiltered.description;
				updatedState[index].attachments = dataFormFiltered.attachments;
				updatedState[index].attachmentsNumber = dataFormFiltered.attachments.length;
				updatedState[index].enableEdit = false;
				return updatedState;
			});

		}catch(err){
			standardErrorMessages(err, translate("Erro ao alterar histórico"), addToast);
		}
	};

	useEffect(() => {
		setEfficiency(data);
	}, [data]);

	return(
		<>
			<RCCGrid container item justifyContent="flex-end" style={{marginBottom: "20px"}}>
				<RCCTooltip color="purple" text={translate("Adicionar eficácia")} position="leftEnd">
					<RCCButton color="purple" 
						icon={{name: "plus", color: "#FFF", size: 18}}
						onClick={() => {
							if(!modalAddEfficiencyRef.current)
								return;
							modalAddEfficiencyRef.current.openModalActionAddEfficiency(actionId, subtitle, () => loadQuery(actionId));
						}}
						size="md"
						layout="isRounded"
						disabled={disabled || (efficiency.length > 0 && efficiency[0].inb007Finalizada === "S")}
					/>
				</RCCTooltip>
			</RCCGrid>
			{!efficiency || efficiency.length === 0 ? (
				<RCCAlert description={translate("Não há nenhum verificação da eficácia")} />
			) : (
				<InfiniteScrollWithoutFetch 
					listElements={efficiency}
					mapper={(inb007: IEfficiency, idx) => (
						<RCCGrid
							key={inb007.inb007Id}
							container
							item
							direction="column"
						>
							<div style={{display: "flex", alignItems: "center", position: "relative"}}>
								<Photo 
									hover={false}
									name={inb007.inb007Efa005Nome}
									link={inb007.inb007Efa005Imagem && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${inb007.inb007Efa005Imagem}`}
									width={40}
									height={40}
								/>
								<ColumnFlex>
									<ContainerUser>
										<Title color="purple" size="md" styleInline="margin: 2px 5px 2px 5px;">
											{inb007.inb007Efa005Nome}
										</Title>
										<Title color="grey" size="xs" styleInline="margin: 2px 5px 2px 5px;">
											{translate("em")} {formatDate(inb007.inb007DataInspecao, currentLanguage)}
										</Title>
										<BadgeEfficiency finished={inb007.inb007Finalizada.toString() === "S"} />
									</ContainerUser>
								</ColumnFlex>
								<div style={{ display: "flex"}}>
									{inb007.attachmentsNumber > 0 && (
										<RCCTooltip
											color={inb007.showAttachments ? "blue" : "grey"}
											text={translate("Anexos")}
											position="leftEnd"
										>
											<RCCButton
												color={inb007.showAttachments ? "blue" : "grey"}
												icon={{
													name: "attachment"
												}}
												layout="isRounded"
												size="md"
												onClick={() => openAttachments(inb007.inb007Id, idx, inb007.attachmentsAlreadyLoaded)}
												loading={inb007.buttonLoading}
											/>
										</RCCTooltip>
									)}
									{(inb007.inb007Efa005Id === user.efa005Id || superUser || administrator) && (
										<RCCTooltip
											color={inb007.enableEdit ? "blue" : "grey"}
											text={translate("Alterar")}
											position="leftEnd"
										>
											<RCCButton
												color={inb007.enableEdit ? "blue" : "grey"}
												icon={{
													name: "pencil"
												}}
												layout="isRounded"
												size="md"
												onClick={() => openEdit(inb007.inb007Id, idx, inb007.attachmentsAlreadyLoaded)}
												disabled={disabled || (inb007.inb007Efa005Id !== user.efa005Id && !superUser && !administrator)}
											/>
										</RCCTooltip>
									)}
									<RCCTooltip
										color="red"
										text={translate("Excluir histórico")}
										position="leftEnd"
									>
										<RCCButton
											color="red"
											icon={{
												name: "trash"
											}}
											layout="isRounded"
											size="md"
											onClick={() => submitDelete(inb007.inb007Id)}
											disabled={disabled || (inb007.inb007Efa005Id !== user.efa005Id && !superUser && !administrator)}
										/>
									</RCCTooltip>
	
								</div>
							</div>

							{inb007.enableEdit ? (
								<FadedDiv>
									<RCCGrid container item>
										<RCCTextArea 
											name={`description[${inb007.inb007Id}]`}
											label={translate("Descrição")}
											maxLength={4000}
											register={register}
											defaultValue={inb007.inb007Descricao}
										/>
									</RCCGrid>
								</FadedDiv>
							): (
								<FadedDiv>
									<SpanComment>
										{inb007.inb007Descricao}
									</SpanComment>
								</FadedDiv>
							)}
							{inb007.showAttachments && (
								<FadedDiv>
									<Attachments 
										name={`attachments[${inb007.inb007Id}]`}
										filesInit={inb007.attachments}
										styleInline={inb007.enableEdit ? "" : "div.dropzone{ display: none }"}
										disabled={disabled || !inb007.enableEdit}
										setValue={setValue}
									/>
								</FadedDiv>
							)}
							{inb007.enableEdit && (
								<>
									<RCCGrid container item justifyContent="flex-end">
										<RCCButton color="green" onClick={handleSubmit((dataForm) => onSubmit(dataForm, inb007.inb007Id))} icon={{
											name: "check"
										}} />
									</RCCGrid>
									<hr />
								</>
							)}
						</RCCGrid>
					)}
				/>
			)}
			<ModalAddEfficiency ref={modalAddEfficiencyRef} />
		</>
	);
};

export default Efficiency;
